import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';

import './styles.scss';
import { Analytics } from 'aws-amplify';

const unrestrictedSurveyDataAPI = process.env.REACT_APP_UNRESTRICTED_SURVEY_DATA_API;

export default function QuestionnaireUploadModal({ show, onClose, participantMetadataRecord, handleSetValidationErrorData, handleOpenValidationErrorModal }) {
    const [isUploading, setIsUploading] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [showExpectedUploadErrors, setShowExpectedUploadErrors] = useState(false);
    const [showUnexpectedUploadErrors, setShowUnexpectedUploadErrors] = useState(false);
    const [warningMessage, setWarningMessage] = useState(null);
    const [showWarningMessage, setShowWarningMessage] = useState(false);
    const [email, setEmail] = useState('');
    const [filePassword, setFilePassword] = useState('');

    const fileInputRef = useRef(null);
    const emailInputRef = useRef(null);

    // Focus on email input when modal opens
    useEffect(() => {
        if (show && emailInputRef.current) {
            setTimeout(() => {
                emailInputRef.current.focus();
            }, 100);
        }
    }, [show]);

    const fetchUploadLink = async () => {
        const response = await axios
            .get(`${unrestrictedSurveyDataAPI}/survey-questionnaire-submission/file-upload-link?participantId=${participantMetadataRecord.id}&participantName=${participantMetadataRecord.participantName}`)
        return { url: response.data.url, fileKey: response.data.fileKey };
    }

    const handleUploadedFileValidation = async (fileKey) => {
        try {
            const response = await axios.post(`${unrestrictedSurveyDataAPI}/survey-questionnaire-submission`, {
                fileKey: fileKey,
                participantName: participantMetadataRecord.participantName,
                participantId: participantMetadataRecord.id,
                submitterEmail: email,
                filePassword: filePassword,
                hsCompanyId: participantMetadataRecord.hubspotCompanyId,
                hsDealId: participantMetadataRecord.hubspotDealId
            });
            processFileUploadResponse(response);
        } catch (error) {
            console.log(error);
            // if bad request, show warning message
            if (error?.response?.status === 400) {
                setErrorMessage(error.response.data.message);
                setShowUnexpectedUploadErrors(true);
            } else {
                setErrorMessage('An error occurred while validating your submission. Please try again later.');
                setShowUnexpectedUploadErrors(true);
            }
        }
    }

    const unexpectedErrorOccuredOnUpload = () => {
        Analytics.record({
            name: 'UnexpectedErrorOnUpload',
            attributes: {
                participantId: participantMetadataRecord.id,
                participantName: participantMetadataRecord.participantName
            }
        });
    }

    const processFileUploadResponse = (response) => {
        if (!response) {
            handleDisplayErrorMessage('An error occurred while processing your submission. Please try again later.');
            unexpectedErrorOccuredOnUpload();
            return;
        }

        const { success, validationErrorCSV, errorMessage, requiredValidation } = response.data;

        if (requiredValidation?.length > 0) {
            handleSetValidationErrorData(requiredValidation);
        }

        if (success) {
            const message = validationErrorCSV
                ? "File uploaded successfully, but there are validation errors. Please view the validation errors in the downloaded file or display them with the link below and re-upload the questionnaire upon completion."
                : "File uploaded successfully pending further review";
            validationErrorCSV ? handleDisplayWarningMessage(message) : handleDisplayingSuccessMessage();
        } else if (errorMessage) {
            handleDisplayErrorMessage(errorMessage);
        } else {
            handleDisplayErrorMessage('Your submission is missing required fields. Please view the validation errors in the downloaded file or display them with the link below and re-upload the questionnaire upon completion.');
        }
    }

    const handleDisplayingSuccessMessage = () => {
        setSuccessMessage('Your submission has passed initial validation and has been received. We may reach out for further details.');
        setShowSuccessMessage(true);
    }

    const handleDisplayWarningMessage = (message) => {
        setWarningMessage(message);
        setShowWarningMessage(true);
    }

    const handleDisplayErrorMessage = (message) => {
        setErrorMessage(message);
        setShowExpectedUploadErrors(true);
    }

    const handleFileUpload = async (event) => {
        setShowSuccessMessage(false);
        setShowUnexpectedUploadErrors(false);
        setShowExpectedUploadErrors(false);
        setShowWarningMessage(false);
        setSuccessMessage('');
        setErrorMessage('');
        setWarningMessage('');
        try {
            event.preventDefault();
            const fileInput = document.querySelector('input[type="file"]');
            if (fileInput.files.length > 0) {
                if (!email) {
                    emailInputRef.current.focus();
                    emailInputRef.current.setCustomValidity('Please enter your email');
                    emailInputRef.current.reportValidity();
                    setIsUploading(false);
                    return;
                }
                setIsUploading(true);
                const { url, fileKey } = await fetchUploadLink();
                const file = fileInput.files[0];
                await axios.put(url, file, {
                    headers: {
                        'Content-Type': file.type
                    }
                });
                await handleUploadedFileValidation(fileKey);
                setIsUploading(false);
            } else {
                fileInputRef.current.focus();
                fileInputRef.current.setCustomValidity('Please select a file to upload');
                fileInputRef.current.reportValidity();
                setIsUploading(false);
            }
        } catch (error) {
            console.log(error);
            setIsUploading(false);
            setShowUnexpectedUploadErrors(true);
            setErrorMessage('An error occurred while processing your submission. Please try again later.');
        }
    }

    const handleModalClose = () => {
        onClose();
        setShowSuccessMessage(false);
        setShowExpectedUploadErrors(false);
        setShowUnexpectedUploadErrors(false);
        setShowWarningMessage(false);
        setSuccessMessage('');
        setErrorMessage('');
        setWarningMessage('');
        setFilePassword('');
        setIsUploading(false);
        setShowWarningMessage(false);
        setEmail('');
    }

    return (
        <Modal
            show={show}
            onHide={handleModalClose}
            className='questionnaire-upload-modal'
            centered
            size="lg"
            aria-labelledby="questionnaire-upload-modal-title"
            aria-describedby="questionnaire-upload-modal-description"
        >
            <Modal.Header closeButton>
                <Modal.Title id="questionnaire-upload-modal-title">Secure Upload</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className='upload-questionnaire-instructions' id="questionnaire-upload-modal-description">
                    <p><b>Automatic validation is performed on upload to confirm submission contains all required inputs.</b> You will receive a confirmation message below if upload is successful. This means we've received your file, but we may still reach out for further details.</p>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); handleFileUpload(e); }}>
                    <div className='file-input-container'>
                        <label htmlFor='file-input' className='file-input-label'>Select File:</label>
                        <input
                            ref={fileInputRef}
                            type='file'
                            id='file-input'
                            className='file-input'
                            aria-describedby="file-input-desc"
                        />
                        <span id="file-input-desc" className="sr-only">Choose the file you want to upload</span>
                    </div>
                    <div className='email-input-container'>
                        <label htmlFor='email-input' className='email-label'>Email <span className='added-context-text'>(Required):</span></label>
                        <input
                            id='email-input'
                            type='email'
                            className='email-input'
                            placeholder='Enter your email'
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            required
                            aria-required="true"
                            ref={emailInputRef}
                        />
                    </div>
                    <div className='password-input-container'>
                        <label htmlFor='password-input' className='password-label'>Password <span className='added-context-text'>(if your file is password protected)</span>:</label>
                        <input
                            id='password-input'
                            type='password'
                            className='password-input'
                            placeholder='Enter file password'
                            value={filePassword}
                            onChange={(event) => setFilePassword(event.target.value)}
                            aria-required="false"
                        />
                    </div>
                </form>

                {showSuccessMessage &&
                    <div className="validation-errors" aria-live="polite">
                        <Alert variant='success'>
                            <h5>Upload Successful</h5>{successMessage}
                        </Alert>
                    </div>
                }

                {showUnexpectedUploadErrors &&
                    <div className="validation-errors">
                        <Alert variant='danger' aria-live="assertive" role="alert">
                            <div>
                                <h5>Upload Incomplete</h5>
                                <div>{errorMessage}</div>
                            </div>
                        </Alert>
                    </div>
                }

                {showExpectedUploadErrors &&
                    <div className="validation-errors">
                        <Alert variant='danger' aria-live="assertive" role="alert">
                            <div>
                                <h5>Upload Incomplete</h5>
                                <div>{errorMessage}</div>
                            </div>
                            <Button
                                className="validation-errors-toggle"
                                size="sm"
                                variant="link"
                                onClick={() => handleOpenValidationErrorModal()}
                                aria-label="View detailed list of issues found in uploaded file"
                            >
                                View Issues in Detail
                            </Button>
                        </Alert>
                    </div>
                }

                {showWarningMessage &&
                    <div className="validation-errors">
                        <Alert variant='warning' aria-live="polite" role="alert">
                            <h5>Upload Successful</h5>
                            <div>{warningMessage}</div>
                            <Button
                                className="validation-errors-toggle"
                                size="sm"
                                variant="link"
                                onClick={() => handleOpenValidationErrorModal()}
                                aria-label="View detailed list of issues found in uploaded file"
                            >
                                View Issues in Detail
                            </Button>
                        </Alert>
                    </div>
                }
            </Modal.Body>
            <Modal.Footer>
                <div className='actions-buttons-container'>
                    <button
                        className='btn btn-secondary'
                        onClick={handleModalClose}
                        aria-label="Cancel upload"
                    >
                        Cancel
                    </button>
                    <button
                        className='btn btn-primary'
                        disabled={isUploading}
                        onClick={handleFileUpload}
                        aria-busy={isUploading}
                        aria-label={isUploading ? "Uploading file, please wait" : "Upload file"}
                    >
                        {isUploading ?
                            <span>Uploading...<Spinner animation="grow" size="sm" aria-hidden="true" /></span> :
                            <span>Upload <i className="fa fa-upload" aria-hidden="true"></i></span>
                        }
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    )
}

QuestionnaireUploadModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    participantMetadataRecord: PropTypes.object.isRequired,
    handleSetValidationErrorData: PropTypes.func.isRequired,
    handleOpenValidationErrorModal: PropTypes.func.isRequired
}